import React from 'react';
import {ExpandedGroupsRef} from '../components/shared/grid/types';
import {
    armViewSnapshot,
    consumeViewSnapshot,
    GridViewKey
} from '../features/grid-view-handoff/slice';
import {useAppDispatch, useAppSelector} from './hooks';

interface GridViewHandoff {
    /* Group keys to re-open on this mount. Stable for the lifetime of the mount. */
    restoredGroupKeys: Array<string>;
    /* Call right before navigating to a form or sub-page this view is expected to return from. */
    armSnapshot: () => void;
}

/**
 * Carries a grid's expanded groups across a navigation away from the page and back.
 *
 * The snapshot is armed on the way out and consumed once on the way back, so reaching the page
 * by any other route starts with everything collapsed.
 *
 * `scope` guards the keys: pass whatever changes their meaning (the grouping mode, for example).
 * A snapshot whose scope no longer matches is dropped instead of opening unrelated groups.
 */
export const useGridViewHandoff = (
    view: GridViewKey,
    scope: string,
    source: React.RefObject<ExpandedGroupsRef>
): GridViewHandoff => {
    const dispatch = useAppDispatch();
    const snapshot = useAppSelector((selector) => selector.gridViewHandoff.snapshots[view]);
    // Captured once. The snapshot leaves the store immediately, but the grid applies these keys
    // lazily (a group only becomes openable once its rows arrive), so the value handed down has
    // to stay stable for the whole mount.
    const [restoredGroupKeys] = React.useState<Array<string>>(() => (
        snapshot && snapshot.scope === scope ? snapshot.expandedGroupKeys : []
    ));
    const consumedRef = React.useRef(false);

    // Consumed on mount and never again. Arming happens while the page is still mounted, right
    // before navigating away, so reacting to later snapshot changes would swallow the snapshot
    // before the navigation even starts.
    React.useEffect(() => {
        if (consumedRef.current) {
            return;
        }

        consumedRef.current = true;
        if (snapshot) {
            dispatch(consumeViewSnapshot(view));
        }
    }, [dispatch, snapshot, view]);

    const armSnapshot = React.useCallback(() => {
        dispatch(armViewSnapshot({
            view,
            scope,
            expandedGroupKeys: source.current?.getExpandedGroupKeys() ?? []
        }));
    }, [dispatch, scope, source, view]);

    return { restoredGroupKeys, armSnapshot };
};
